/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Monthly Team-Work Dashboard — ported from `dashboard.jsx`.
 * Gantt (overall + by team), People×Months, Projects×Months, burden heatmap.
 *
 * The month axis is MULTI-YEAR: derived from the real ISO dates of every
 * timeline row (capped at 6 years, always including the current month) —
 * never from the legacy 0-11 month ints, which alias every year onto 2026.
 */
import { useMemo, useState, useRef, useEffect } from 'react';
import React from 'react';
import { useData } from '../context/AppDataContext';
import {
  Ico, Avatar, STATUS_META, relTime, useToast,
  taskDates, fmtShortDate, taskMonthKeys, monthKeyLabel, todayMonthKey,
  isoFirstOfMonthKey, isoLastOfMonthKey,
} from '../components/ui';
import { downloadCsv, dateStamp } from '../lib/exportUtils';
import { apiClient, ApiClientError } from '../lib/apiClient';
import { assigneesOf as taskAssignees, orderCategories } from '../lib/workload';

/** Full set of landmark-phase categories shown in the Gantt landmark picker. */
const LANDMARK_CATEGORIES = ['Protocol', 'Sample size', 'Ethics', 'CRF/eCRF', 'REDCap', 'Initiation meeting', 'Recruitment', 'Follow-up', 'Monitoring', 'Data cleaning', 'Analysis', 'Summary report', 'Manuscript'];

/* Day-precision positioning along the multi-year month axis (Gantt bars). */
const DAY_MS = 86400000;
const isoTime = (iso: string) => new Date(iso + 'T00:00:00').getTime();
function axisSpan(monthKeys: number[]) {
  const startIso = isoFirstOfMonthKey(monthKeys[0]);
  const endIso = isoLastOfMonthKey(monthKeys[monthKeys.length - 1]);
  const totalDays = Math.round((isoTime(endIso) - isoTime(startIso)) / DAY_MS) + 1;
  return { startIso, totalDays };
}
function dayOffset(iso: string, startIso: string, totalDays: number) {
  const off = Math.round((isoTime(iso) - isoTime(startIso)) / DAY_MS);
  return Math.max(0, Math.min(totalDays - 1, Number.isNaN(off) ? 0 : off));
}

// Everyone allocated to a task (splits ∪ owner) + the matrix category ordering now
// live in lib/workload (taskAssignees = assigneesOf) so the Dashboard, the Team
// Workload tab, the Portfolio panel and the profile portfolio never drift.

export function Dashboard({ openProject, editTask }: { openProject: (id: string, tab?: string) => void; editTask: (t: any) => void }) {
  const D = useData();
  const [view, setView] = useState('gantt-project');
  // Shipped landmark defaults (senior request: include "Sample size").
  const DEFAULT_LANDMARKS = ['Protocol', 'Sample size', 'Ethics', 'Initiation meeting', 'Recruitment', 'Follow-up', 'Analysis', 'Summary report'];
  // Each user can save their own defaults (persisted in profileJson.landmarkDefaults).
  const [myDefaults, setMyDefaults] = useState<string[]>(DEFAULT_LANDMARKS);
  const [landmarkCats, setLandmarkCats] = useState<Set<string>>(new Set(DEFAULT_LANDMARKS));
  const [savingDefaults, setSavingDefaults] = useState(false);
  // The Gantt landmark picker is live from first paint, so guard the async load
  // from overwriting a selection the user already started touching.
  const landmarksTouched = useRef(false);
  // On mount, load this user's saved landmark defaults (falling back to shipped).
  useEffect(() => {
    let cancelled = false;
    apiClient.users.me.profile().then((res) => {
      const ld = res.profile?.landmarkDefaults;
      if (!cancelled && Array.isArray(ld) && ld.length) {
        setMyDefaults(ld);
        if (!landmarksTouched.current) setLandmarkCats(new Set(ld));
      }
    }).catch(() => { /* keep shipped defaults */ });
    return () => { cancelled = true; };
  }, []);
  const [landmarkOpen, setLandmarkOpen] = useState(false);
  // Menu is rendered position:fixed (not absolute) so it escapes the `.dashboard-shell { overflow:hidden }`
  // clip that otherwise hides the dropdown when it extends past the shell box.
  const landmarkBtnRef = useRef<HTMLButtonElement>(null);
  const [landmarkPos, setLandmarkPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const openLandmark = () => {
    setLandmarkOpen((o) => {
      const next = !o;
      if (next && landmarkBtnRef.current) {
        const r = landmarkBtnRef.current.getBoundingClientRect();
        setLandmarkPos({ top: r.bottom + 4, left: r.left });
      }
      return next;
    });
  };
  const toggleLandmark = (cat: string) => {
    landmarksTouched.current = true;
    setLandmarkCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };
  const saveMyDefaults = async () => {
    const next = [...landmarkCats];
    setSavingDefaults(true);
    try {
      await apiClient.users.me.saveProfile({ landmarkDefaults: next });
      setMyDefaults(next);
      toast('Saved as your default landmark phases');
    } catch { toast('Could not save your default landmarks'); }
    finally { setSavingDefaults(false); }
  };
  const [filterProject, setFilterProject] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const items = useMemo(() => D.timeline.filter((t) =>
    (t as any).rowType !== 'phase' // phase dividers are visual section headers, not tasks
    && (filterProject === 'all' || t.project === filterProject)
    && (filterCategory === 'all' || t.category === filterCategory)
    && (filterStatus === 'all' || t.status === filterStatus)), [D.timeline, filterProject, filterCategory, filterStatus]);

  const projectsById = useMemo(() => Object.fromEntries(D.projects.map((p) => [p.id, p])), [D.projects]);
  const usersById = useMemo(() => Object.fromEntries(D.team.map((u) => [u.id, u])), [D.team]);

  // Multi-year month axis from ALL timeline rows (filter-stable: filters
  // narrow the tasks, not the columns). Always contains the current month;
  // capped at 6 years, never cutting the current month out.
  const todayK = todayMonthKey();
  const monthKeys = useMemo(() => {
    let minK = todayK; let maxK = todayK;
    D.timeline.forEach((t) => {
      if ((t as any).rowType === 'phase') return; // dividers carry no real dates
      const { sK, eK } = taskMonthKeys(t);
      if (sK < minK) minK = sK;
      if (eK > maxK) maxK = eK;
    });
    minK = Math.max(minK, todayK - 71);
    maxK = Math.min(maxK, minK + 71);
    return Array.from({ length: maxK - minK + 1 }, (_, i) => minK + i);
  }, [D.timeline, todayK]);
  const monthLabels = useMemo(() => monthKeys.map(monthKeyLabel), [monthKeys]);
  const currentIdx = monthKeys.indexOf(todayK);

  const cellMap = useMemo(() => {
    const base = monthKeys[0];
    const N = monthKeys.length;
    const m: Record<string, any[][]> = {};
    items.forEach((t) => {
      // Project views key by project; person views key by EVERY allocated
      // person (splits ∪ owner) so split members aren't invisible.
      const rowIds = view === 'projects' ? [t.project] : taskAssignees(t);
      const { sK, eK } = taskMonthKeys(t);
      for (const rowId of rowIds) {
        if (!rowId) continue;
        if (!m[rowId]) m[rowId] = Array.from({ length: N }, () => []);
        for (let k = Math.max(sK, base); k <= Math.min(eK, base + N - 1); k++) {
          m[rowId][k - base].push({ ...t, continuesLeft: k > sK, continuesRight: k < eK });
        }
      }
    });
    return m;
  }, [items, view, monthKeys]);

  const rows: any[] = view === 'projects' ? D.projects : D.team;

  const burdenClass = (count: number) => {
    if (!count) return 'bn-free';
    if (count <= 2) return 'bn-light';
    if (count <= 4) return 'bn-moderate';
    return 'bn-heavy';
  };
  const burdenLabel = (count: number) => {
    if (!count) return 'Free';
    if (count <= 2) return 'Light';
    if (count <= 4) return 'Moderate';
    return 'Heavy';
  };

  // --- Server-side LaTeX PDF of the People × Months workload matrix (replaces window.print) ---
  const toast = useToast();
  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      // The server PDF matrix is capped at 12 month columns — export a rolling
      // window that starts at the current month (clamped into the axis).
      const winLen = Math.min(12, monthKeys.length);
      const winStart = Math.min(Math.max(0, currentIdx), monthKeys.length - winLen);
      const winKeys = monthKeys.slice(winStart, winStart + winLen);
      const base = winKeys[0];
      const rowsData = D.team.map((u) => {
        const counts = Array.from({ length: winLen }, () => 0);
        items.forEach((t: any) => {
          if (!taskAssignees(t).includes(u.id)) return; // count every person allocated, not just the owner
          const { sK, eK } = taskMonthKeys(t);
          for (let k = Math.max(sK, base); k <= Math.min(eK, base + winLen - 1); k++) counts[k - base]++;
        });
        return { name: u.name, role: u.role as string | undefined, counts };
      });
      const totalsByUser = rowsData.map((r) => r.counts.reduce((a, b) => a + b, 0));
      const activeTaskMonths = totalsByUser.reduce((a, b) => a + b, 0);
      const heaviestIdx = totalsByUser.reduce((best, t, i, arr) => (t > arr[best] ? i : best), 0);
      const nowIdx = Math.min(Math.max(0, todayK - base), winLen - 1); // current-month column in the window
      const overloadedCount = rowsData.filter((r) => r.counts[nowIdx] >= 5).length;
      const freeCount = rowsData.filter((r) => r.counts[nowIdx] === 0).length;
      const dateText = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const blob = await apiClient.dashboard.pdf({
        title: 'Monthly Team-Work Dashboard',
        subtitle: `Task-months per team member · ${monthKeyLabel(winKeys[0])} – ${monthKeyLabel(winKeys[winLen - 1])}`,
        dateText,
        months: winKeys.map(monthKeyLabel),
        rows: rowsData,
        summary: {
          activeTaskMonths,
          heaviestName: rowsData[heaviestIdx]?.name,
          heaviestTotal: totalsByUser[heaviestIdx],
          overloadedCount,
          freeCount,
          periodLabel: monthKeyLabel(winKeys[nowIdx]),
        },
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `team-dashboard-${dateStamp()}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      const m = err instanceof ApiClientError ? (err.body?.message ?? '') : String((err as any)?.message || '');
      toast(/not installed|Unavailable|503/i.test(m) ? 'Server LaTeX is unavailable — cannot generate the PDF.' : 'Dashboard PDF failed — please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>Monthly Team-Work Dashboard</h2>
          <div className="sub">Who is doing what, in which project, in which month — aggregated live from every project timeline.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={exportPdf} disabled={pdfBusy}><Ico.print /> {pdfBusy ? 'Generating…' : 'Export PDF'}</button>
          <button className="btn primary" onClick={() => {
            if (!items.length) { return; }
            downloadCsv(items, {
              Project: (t: any) => projectsById[t.project]?.code ?? t.project,
              Task: (t: any) => t.name,
              Assignee: (t: any) => usersById[t.user]?.name ?? t.user,
              Category: (t: any) => t.category,
              Status: (t: any) => STATUS_META[t.status]?.label ?? t.status,
              Start: (t: any) => taskDates(t).startDate,
              End: (t: any) => taskDates(t).endDate,
            }, `dashboard-tasks-${dateStamp()}.csv`);
          }}><Ico.dl /> Export CSV / Excel</button>
        </div>
      </div>

      <div className="dashboard-shell">
        <div className="dashboard-toolbar">
          <div className="seg" role="tablist">
            <button className={view === 'gantt-project' ? 'on' : ''} onClick={() => setView('gantt-project')}>Overall Gantt</button>
            <button className={view === 'gantt-user' ? 'on' : ''} onClick={() => setView('gantt-user')}>Gantt by Team</button>
            <button className={view === 'people' ? 'on' : ''} onClick={() => setView('people')}>People × Months</button>
            <button className={view === 'projects' ? 'on' : ''} onClick={() => setView('projects')}>Projects × Months</button>
            <button className={view === 'heatmap' ? 'on' : ''} onClick={() => setView('heatmap')}>Burden heatmap</button>
            <button className={view === 'equity' ? 'on' : ''} onClick={() => setView('equity')}>Workload equity</button>
          </div>
          {/* The equity view always aggregates across ALL projects (per spec), so
              the project/phase/status filters don't apply — hide them there to
              avoid the "filter has no effect" confusion. */}
          {view !== 'equity' && (
          <div className="filters">
            <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
              <option value="all">All projects</option>
              {D.projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.shortTitle}</option>)}
            </select>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="all">All phases</option>
              {Object.keys(D.categories).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All statuses</option>
              {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
            {(filterProject !== 'all' || filterCategory !== 'all' || filterStatus !== 'all') && (
              <button className="btn ghost sm" onClick={() => { setFilterProject('all'); setFilterCategory('all'); setFilterStatus('all'); }}>Reset</button>
            )}
          </div>
          )}
          <div style={{ flex: 1 }} />
          <div className="legend">
            <span className="swatch" style={{ background: 'var(--bn-free)', border: '1px solid var(--border)' }} /> Free
            <span className="swatch" style={{ background: 'var(--bn-light)' }} /> Light
            <span className="swatch" style={{ background: 'var(--bn-moderate)' }} /> Moderate
            <span className="swatch" style={{ background: 'var(--bn-heavy)' }} /> Heavy
          </div>
        </div>

        {view === 'gantt-project' && (
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', background: 'var(--surface-alt)', display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Landmark phases</span>
            <button ref={landmarkBtnRef} className="btn sm" onClick={openLandmark} style={{ minWidth: 200, justifyContent: 'space-between' }}>
              <span>{landmarkCats.size} of {LANDMARK_CATEGORIES.length} selected</span>
              <Ico.chevDown />
            </button>
            {landmarkCats.size > 0 && landmarkCats.size <= 4 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {[...landmarkCats].map((cat) => {
                  const tone = (D.categories[cat] || { tone: 'slate' }).tone;
                  return <span key={cat} className="chip" data-tone={tone}><span className="dot" />{cat}</span>;
                })}
              </div>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className="btn ghost sm" title="Reset to your saved defaults" onClick={() => setLandmarkCats(new Set(myDefaults))}>Reset</button>
              <button className="btn sm" disabled={savingDefaults} title="Save the current selection as your personal default" onClick={() => void saveMyDefaults()}>{savingDefaults ? 'Saving…' : 'Save as my default'}</button>
            </div>

            {landmarkOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setLandmarkOpen(false)} />
                <div style={{ position: 'fixed', top: landmarkPos.top, left: landmarkPos.left, zIndex: 10, background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 8, boxShadow: 'var(--shadow-md)', minWidth: 260, padding: 6 }}>
                  <div style={{ padding: '6px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>Show on Gantt</div>
                  <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 320, overflow: 'auto', padding: 4 }}>
                    {LANDMARK_CATEGORIES.map((cat) => {
                      const on = landmarkCats.has(cat);
                      const tone = (D.categories[cat] || { tone: 'slate' }).tone;
                      return (
                        <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12.5 }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-alt)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}>
                          <input type="checkbox" checked={on} onChange={() => toggleLandmark(cat)} style={{ accentColor: `var(--t-${tone}-fg)` }} />
                          <span style={{ width: 10, height: 10, borderRadius: 2, background: `var(--t-${tone}-fg)`, display: 'inline-block' }} />
                          <span style={{ flex: 1 }}>{cat}</span>
                          {DEFAULT_LANDMARKS.includes(cat) && <span style={{ fontSize: 9, color: 'var(--text-3)' }}>default</span>}
                        </label>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 4, padding: 6, borderTop: '1px solid var(--border)' }}>
                    <button className="btn ghost sm" onClick={() => { landmarksTouched.current = true; setLandmarkCats(new Set(LANDMARK_CATEGORIES)); }}>All</button>
                    <button className="btn ghost sm" onClick={() => { landmarksTouched.current = true; setLandmarkCats(new Set()); }}>None</button>
                    <div style={{ flex: 1 }} />
                    <button className="btn primary sm" onClick={() => setLandmarkOpen(false)}><Ico.check /> Done</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="scroll-x">
          {view === 'people' && <GridMonth rows={rows} months={monthLabels} currentIdx={currentIdx} cellMap={cellMap} projectsById={projectsById} burdenClass={burdenClass} burdenLabel={burdenLabel} renderRowHeader={(u: any) => (
            <>
              <Avatar user={u} />
              <div className="ru-meta">
                <div className="name">{u.name}</div>
                <div className="role">{u.role}</div>
              </div>
            </>
          )} mode="people" openProject={openProject} editTask={editTask} />}

          {view === 'projects' && <GridMonth rows={rows} months={monthLabels} currentIdx={currentIdx} cellMap={cellMap} projectsById={projectsById} burdenClass={() => ''} burdenLabel={() => ''} renderRowHeader={(p: any) => (
            <div className="ru-meta">
              <div className="name">{p.code}</div>
              <div className="role" title={p.title}>{p.shortTitle}</div>
            </div>
          )} mode="projects" usersById={usersById} openProject={openProject} editTask={editTask} />}

          {view === 'gantt-project' && <GanttView items={items} months={monthLabels} monthKeys={monthKeys} currentIdx={currentIdx} projectsById={projectsById} usersById={usersById} editTask={editTask} openProject={openProject} groupBy="project" landmarkCats={landmarkCats} />}

          {view === 'gantt-user' && <GanttView items={items} months={monthLabels} monthKeys={monthKeys} currentIdx={currentIdx} projectsById={projectsById} usersById={usersById} editTask={editTask} groupBy="user" />}

          {view === 'heatmap' && <HeatmapView rows={D.team} months={monthLabels} cellMap={cellMap} burdenClass={burdenClass} burdenLabel={burdenLabel} />}

          {view === 'equity' && <WorkloadEquityView />}
        </div>
      </div>

      <BurdenSummary cellMap={cellMap} months={monthLabels} currentIdx={currentIdx} />
    </div>
  );
}

function GridMonth({ rows, months, currentIdx, cellMap, projectsById, burdenClass, burdenLabel, renderRowHeader, mode, usersById, editTask }: any) {
  const D = useData();
  // The .grid-month CSS hardcodes 12 columns — override for the dynamic axis.
  return (
    <div className="grid-month" style={{ minWidth: 200 + months.length * 108, gridTemplateColumns: `200px repeat(${months.length}, minmax(108px, 1fr))` }}>
      <div className="h-cell corner">{mode === 'projects' ? 'Project' : 'Team member'}</div>
      {months.map((m: string, idx: number) => <div className="h-cell" key={m} title={idx === currentIdx ? 'Current month' : undefined} style={idx === currentIdx ? { background: 'var(--accent-soft)', color: 'var(--accent)', fontWeight: 700 } : undefined}>{m}</div>)}
      {rows.map((row: any) => (
        <React.Fragment key={row.id}>
          <div className="row-h">{renderRowHeader(row)}</div>
          {months.map((m: string, idx: number) => {
            const tasks = (cellMap[row.id] || [])[idx] || [];
            const bn = burdenClass(tasks.length);
            return (
              <div className={'cell ' + bn} key={m + row.id} style={idx === currentIdx ? { boxShadow: 'inset 1px 0 0 var(--accent), inset -1px 0 0 var(--accent)' } : undefined} title={mode === 'people' ? `${burdenLabel(tasks.length)} (${tasks.length} task${tasks.length === 1 ? '' : 's'})` : ''}>
                {tasks.map((t: any, ti: number) => {
                  const p = projectsById[t.project];
                  const tone = (D.categories[t.category] || { tone: 'slate' }).tone;
                  const updater = t.lastUpdate ? D.team.find((u) => u.id === t.lastUpdate.by) : null;
                  return (
                    <div
                      key={t.id + '-' + ti}
                      className={'taskcard' + (t.continuesLeft ? ' continues-left' : '') + (t.continuesRight ? ' continues-right' : '')}
                      data-tone={tone}
                      onClick={() => editTask(t)}
                      title={t.lastUpdate ? `Last updated by ${updater?.name} · ${t.lastUpdate.at}` : ''}
                    >
                      <div className="code" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>{p ? p.code : '—'}</span>
                        {t.status === 'completed' && <span title="Submitted" style={{ color: 'var(--t-green-fg)', fontWeight: 700 }}>✓</span>}
                      </div>
                      <div className="nm" style={{ textDecoration: t.status === 'completed' ? 'line-through' : 'none' }}>{t.name}</div>
                      <div className="meta">
                        <span className={'status-dot ' + t.status} />
                        {mode === 'projects' && usersById[t.user] && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: 'var(--text-2)' }}>
                            <Avatar user={usersById[t.user]} size="sm" />
                            {usersById[t.user].initials}
                          </span>
                        )}
                        {t.lastUpdate && (
                          <span style={{ marginLeft: 'auto', fontSize: 9.5, color: 'var(--text-3)' }}>
                            {relTime(t.lastUpdate.at)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

function GanttView({ items, months, monthKeys, currentIdx, projectsById, editTask, openProject, groupBy, landmarkCats }: any) {
  const D = useData();
  const isProject = groupBy === 'project';
  if (isProject) {
    return <GanttFlat items={items} months={months} monthKeys={monthKeys} currentIdx={currentIdx} openProject={openProject} landmarkCats={landmarkCats} />;
  }
  const rows = D.team;
  const rowKey = 'user';

  const byRow = useMemo(() => {
    const m: Record<string, any[]> = {};
    items.forEach((t: any) => {
      const k = t[rowKey];
      if (!m[k]) m[k] = [];
      m[k].push(t);
    });
    return m;
  }, [items, groupBy]);

  const { startIso, totalDays } = axisSpan(monthKeys);
  const colStyle = { gridTemplateColumns: `repeat(${months.length}, 1fr)` };

  return (
    <div className="gantt" style={{ minWidth: 240 + months.length * 100, gridTemplateColumns: '240px 1fr' }}>
      <div className="g-head" style={{ width: 240 }}>Team member</div>
      <div className="g-track-head" style={colStyle}>
        {months.map((m: string, i: number) => <div key={m} title={i === currentIdx ? 'Current month' : undefined} style={i === currentIdx ? { background: 'var(--accent-soft)', color: 'var(--accent)', fontWeight: 700 } : undefined}>{m}</div>)}
      </div>
      {rows.map((row: any) => {
        const tasks = byRow[row.id] || [];
        // Spans once per task — not inside the O(n²) lane-conflict scan.
        const withSpans = tasks.map((t: any) => ({ t, span: taskMonthKeys(t) }));
        const lanes: Array<Array<{ sK: number; eK: number }>> = [];
        const placed = withSpans.map(({ t, span }) => {
          let lane = 0;
          for (;;) {
            const conflict = (lanes[lane] || []).some((o) => !(span.eK < o.sK || span.sK > o.eK));
            if (!conflict) {
              if (!lanes[lane]) lanes[lane] = [];
              lanes[lane].push(span);
              return { ...t, lane };
            }
            lane++;
          }
        });
        const laneCount = Math.max(1, lanes.length);
        const heightPx = laneCount * 22 + 12;
        const done = tasks.filter((t: any) => t.status === 'completed').length;

        return (
          <React.Fragment key={row.id}>
            <div className="g-rowh" style={{ minHeight: heightPx, width: 240, paddingLeft: 12 }}>
              <Avatar user={row} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{row.name}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{tasks.length} task{tasks.length === 1 ? '' : 's'} · {done} done</div>
              </div>
            </div>
            <div className="g-track" style={{ minHeight: heightPx, ...colStyle }}>
              {months.map((_m: string, i: number) => <div className="gcol" key={i} style={i === currentIdx ? { background: 'var(--accent-soft)' } : undefined} />)}
              {placed.map((t: any) => {
                const td = taskDates(t);
                const sd = dayOffset(td.startDate, startIso, totalDays);
                const ed = dayOffset(td.endDate, startIso, totalDays);
                const tone = (D.categories[t.category] || { tone: 'slate' }).tone;
                const p = projectsById[t.project];
                const left = (sd / totalDays) * 100;
                const width = ((ed - sd + 1) / totalDays) * 100;
                return (
                  <div key={t.id} className="g-bar" data-tone={tone}
                    style={{ left: `calc(${left}% + 2px)`, width: `calc(${width}% - 4px)`, top: 6 + t.lane * 22, opacity: t.status === 'completed' ? 0.65 : 1 }}
                    onClick={() => editTask(t)}
                    title={`${p?.code} — ${t.name} · ${fmtShortDate(td.startDate)} → ${fmtShortDate(td.endDate)} · ${(STATUS_META[t.status] || { label: t.status }).label}`}>
                    <span style={{ fontWeight: 700, fontSize: 10 }}>{p?.code}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: t.status === 'completed' ? 'line-through' : 'none' }}>{t.name}</span>
                    {t.status === 'completed' && <span style={{ marginLeft: 'auto', fontSize: 10 }}>✓</span>}
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function GanttFlat({ items, months, monthKeys, currentIdx, openProject, landmarkCats }: any) {
  const D = useData();
  const projectTones = ['green', 'violet', 'amber', 'blue', 'pink', 'cyan', 'orange', 'purple'];
  const showCats: Set<string> = landmarkCats || new Set(['Recruitment', 'Follow-up', 'Analysis', 'Summary report']);
  const { startIso, totalDays } = axisSpan(monthKeys);

  const bands = D.projects
    .map((p, idx) => {
      const allTasks = items.filter((t: any) => t.project === p.id);
      if (allTasks.length === 0) return null;
      const spans = allTasks.map((t: any) => taskMonthKeys(t));
      const start = Math.min(...spans.map((s: any) => s.sK));
      const end = Math.max(...spans.map((s: any) => s.eK));
      const landmarks = allTasks.filter((t: any) => showCats.has(t.category));
      return { project: p, tone: projectTones[idx % projectTones.length], start, end, landmarks };
    })
    .filter(Boolean)
    .sort((a: any, b: any) => a.start - b.start || a.end - b.end) as any[];

  const rowH = 64;
  const headerH = 32;
  const colStyle = { gridTemplateColumns: `repeat(${months.length}, 1fr)` };

  return (
    <div style={{ minWidth: months.length * 100 }}>
      <div style={{ display: 'grid', ...colStyle, borderBottom: '1px solid var(--border)' }}>
        {months.map((m: string, i: number) => (
          <div key={m} title={i === currentIdx ? 'Current month' : undefined} style={{ background: i === currentIdx ? 'var(--accent-soft)' : 'var(--surface-alt)', color: i === currentIdx ? 'var(--accent)' : 'var(--text-2)', padding: '8px 6px', fontSize: 11, fontWeight: i === currentIdx ? 800 : 600, textTransform: 'uppercase', letterSpacing: '0.04em', borderRight: '1px solid var(--border)', textAlign: 'center', height: headerH, lineHeight: '16px' }}>
            {m}
          </div>
        ))}
      </div>
      <div>
        {bands.map((b: any) => {
          const projTasks = D.timeline.filter((t) => t.project === b.project.id);
          const spanStart = Math.min(...projTasks.map((t) => dayOffset(taskDates(t).startDate, startIso, totalDays)));
          const spanEnd = Math.max(...projTasks.map((t) => dayOffset(taskDates(t).endDate, startIso, totalDays)));
          const left = (spanStart / totalDays) * 100;
          const width = ((spanEnd - spanStart + 1) / totalDays) * 100;
          return (
            <div key={b.project.id} style={{ position: 'relative', height: rowH, display: 'grid', ...colStyle, borderBottom: '1px solid var(--border)' }}>
              {months.map((_: string, i: number) => <div key={i} style={{ borderRight: '1px solid var(--border)', height: rowH, ...(i === currentIdx ? { background: 'var(--accent-soft)' } : {}) }} />)}

              <div
                onClick={() => openProject && openProject(b.project.id)}
                title={`${b.project.code} — ${b.project.title}`}
                style={{ position: 'absolute', left: `calc(${left}% + 4px)`, width: `calc(${width}% - 8px)`, top: rowH / 2 - 10, height: 20, background: `var(--t-${b.tone}-bg)`, borderLeft: `3px solid var(--t-${b.tone}-fg)`, borderRadius: 4, cursor: 'pointer' }}
              />

              <div
                onClick={() => openProject && openProject(b.project.id)}
                style={{ position: 'absolute', left: `calc(${left}% + 10px)`, top: rowH / 2 - 28, fontSize: 11, fontWeight: 700, color: `var(--t-${b.tone}-fg)`, cursor: 'pointer', zIndex: 3, pointerEvents: 'auto', whiteSpace: 'nowrap' }} className="mono">
                {b.project.code}
                <span style={{ marginLeft: 6, color: 'var(--text-2)', fontWeight: 500 }}>{b.project.shortTitle}</span>
              </div>

              {b.landmarks.map((t: any) => {
                const td = taskDates(t);
                const sd = dayOffset(td.startDate, startIso, totalDays);
                const ed = dayOffset(td.endDate, startIso, totalDays);
                const lLeft = (sd / totalDays) * 100;
                const lWidth = ((ed - sd + 1) / totalDays) * 100;
                const catTone = (D.categories[t.category] || { tone: 'slate' }).tone;
                const isPoint = (ed - sd) <= 1;
                return (
                  <div key={t.id}
                    title={`${t.category}: ${t.name} · ${fmtShortDate(td.startDate)}${isPoint ? '' : ' → ' + fmtShortDate(td.endDate)}`}
                    style={{ position: 'absolute', left: `calc(${lLeft}% + ${isPoint ? 1 : 4}px)`, width: isPoint ? 10 : `calc(${lWidth}% - 8px)`, minWidth: isPoint ? 10 : 22, top: rowH / 2 - 8, height: 16, background: `var(--t-${catTone}-fg)`, borderRadius: isPoint ? '50%' : 3, color: '#fff', fontSize: 10, fontWeight: 600, padding: isPoint ? 0 : '0 6px', display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', whiteSpace: 'nowrap', opacity: t.status === 'completed' ? 0.55 : 1, zIndex: 2, letterSpacing: '0.02em', border: isPoint ? '2px solid var(--surface)' : 'none', boxShadow: isPoint ? '0 0 0 1px var(--text)' : 'none' }}>
                    {!isPoint && t.status === 'completed' && <span style={{ fontSize: 10 }}>✓</span>}
                    {!isPoint && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.category}</span>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 14, padding: 10, fontSize: 11, color: 'var(--text-2)', borderTop: '1px solid var(--border)', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontWeight: 600 }}>Showing:</span>
        {[...showCats].map((cat) => {
          const tone = (D.categories[cat] || { tone: 'slate' }).tone;
          return (
            <span key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 14, height: 8, borderRadius: 2, background: `var(--t-${tone}-fg)` }} /> {cat}
            </span>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>Click a project band to open it</span>
      </div>
    </div>
  );
}

function HeatmapView({ rows, months, cellMap, burdenClass, burdenLabel }: any) {
  // 'Jan 2026' → 'Jan ’26' (the year must stay visible on a multi-year axis).
  const short = (m: string) => `${m.split(' ')[0]} ’${m.slice(-2)}`;
  return (
    <div className="heatmap" style={{ minWidth: 200 + months.length * 80, gridTemplateColumns: `200px repeat(${months.length}, 1fr)` }}>
      <div className="h-cell" style={{ textAlign: 'left', paddingLeft: 12 }}>Team member</div>
      {months.map((m: string) => <div className="h-cell" key={m}>{short(m)}</div>)}
      {rows.map((u: any) => (
        <React.Fragment key={u.id}>
          <div className="row-h">
            <Avatar user={u} size="sm" />
            <div>
              <div style={{ fontWeight: 600 }}>{u.name}</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{u.role}</div>
            </div>
          </div>
          {months.map((m: string, idx: number) => {
            const tasks = (cellMap[u.id] || [])[idx] || [];
            return (
              <div key={m + u.id} className={'hcell ' + burdenClass(tasks.length)}>
                <div className="tabular">{tasks.length}</div>
                <div className="lab">{burdenLabel(tasks.length)}</div>
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * Workload equity — "every member should cover every job type".
 * Counts each member's tasks PER JOB CATEGORY across ALL projects (every
 * allocated person = splits ∪ owner, matching the rest of the dashboard;
 * milestone/phase rows carry no work and are excluded). Renders a
 * users × category MATRIX of counts, a per-user TOTAL bar chart, and a fairness
 * indicator so an imbalance (and any "never done this job" gaps) is obvious.
 */
function WorkloadEquityView() {
  const D = useData();

  const { counts, catTotals, userTotals, activeCats } = useMemo(() => {
    // counts[userId][category] = number of tasks the member is allocated to in that category.
    const counts: Record<string, Record<string, number>> = {};
    const catTotals: Record<string, number> = {};
    // Count EVERY allocated person (splits ∪ owner), matching the rest of the
    // dashboard (cellMap / export use taskAssignees) so split co-owners and
    // owner-less-but-split tasks aren't invisible. Only known team members are
    // counted, so the column totals reconcile with the per-member grand total.
    const teamIds = new Set(D.team.map((u) => u.id));
    D.timeline.forEach((t: any) => {
      // Milestone / phase rows are markers, not work.
      if (t.rowType === 'milestone' || t.rowType === 'phase') return;
      const cat = t.category || 'Uncategorised';
      for (const uid of taskAssignees(t)) {
        if (!teamIds.has(uid)) continue;
        if (!counts[uid]) counts[uid] = {};
        counts[uid][cat] = (counts[uid][cat] || 0) + 1;
        catTotals[cat] = (catTotals[cat] || 0) + 1;
      }
    });
    // Columns: only categories that actually have ≥1 task (keeps the matrix readable),
    // ordered by the canonical D.categories order, then any extras alphabetically.
    const activeCats = orderCategories(D.categories, catTotals);
    const userTotals: Record<string, number> = {};
    D.team.forEach((u) => {
      userTotals[u.id] = activeCats.reduce((s, c) => s + (counts[u.id]?.[c] || 0), 0);
    });
    return { counts, catTotals, userTotals, activeCats };
  }, [D.timeline, D.categories, D.team]);

  const team = D.team;
  const grandTotal = team.reduce((s, u) => s + userTotals[u.id], 0);
  const totalsArr = team.map((u) => userTotals[u.id]);
  const maxTotal = Math.max(1, ...totalsArr);
  const minTotal = team.length ? Math.min(...totalsArr) : 0;
  const peakTotal = team.length ? Math.max(...totalsArr) : 0;
  const avg = team.length ? grandTotal / team.length : 0;
  // Under-/over-loaded thresholds (senior asked for an obvious fairness flag).
  const lowCut = avg * 0.6;
  const highCut = avg * 1.4;
  const loadTone = (total: number): { tone: string; label: string } | null => {
    if (avg <= 0) return null;
    if (total < lowCut) return { tone: 'amber', label: 'Under-loaded' };
    if (total > highCut) return { tone: 'red', label: 'Over-loaded' };
    return null;
  };
  // Bars sorted heaviest-first so the imbalance reads top-to-bottom.
  const barRows = [...team].sort((a, b) => userTotals[b.id] - userTotals[a.id]);
  const toneOf = (cat: string) => (D.categories[cat] || { tone: 'slate' }).tone;

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
        Counts each member&apos;s tasks by job type across all projects — every member should cover every job type.
        Empty cells (·) show a job a member has never done; milestone &amp; phase markers are excluded.
      </div>

      {/* Fairness indicator */}
      <div className="stat" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div className="label">Fairness</div>
          <div className="v tabular" style={{ fontSize: 18 }}>
            Avg {avg.toFixed(1)} tasks/user
            <span style={{ fontSize: 13, color: 'var(--text-2)', fontWeight: 500, marginLeft: 8 }}>
              · range {minTotal}–{peakTotal}
            </span>
          </div>
          <div className="d">{grandTotal} owned tasks across {team.length} members · {activeCats.length} job types</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className="chip" data-tone="amber"><span className="dot" />Under-loaded &lt; {Math.round(lowCut * 10) / 10}</span>
          <span className="chip" data-tone="red"><span className="dot" />Over-loaded &gt; {Math.round(highCut * 10) / 10}</span>
        </div>
      </div>

      {/* (A) Matrix: users × job category */}
      <div className="card" style={{ overflow: 'auto' }}>
        <div className="card-h"><h3>Jobs per member by type</h3><div className="sub">Blank (·) = never done this job type · darker = more done</div></div>
        <div className="card-b flush">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 10px', position: 'sticky', left: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', minWidth: 160, zIndex: 1 }}>Team member</th>
                {activeCats.map((c) => (
                  <th key={c} title={c} className="num" style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)', color: `var(--t-${toneOf(c)}-fg)`, fontWeight: 700, whiteSpace: 'nowrap', maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</th>
                ))}
                <th className="num" style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', fontWeight: 700 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {team.map((u) => {
                const flag = loadTone(userTotals[u.id]);
                return (
                  <tr key={u.id}>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar user={u} size="sm" />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {u.name}
                            {flag && <span className="chip" data-tone={flag.tone} style={{ fontSize: 9.5, padding: '1px 6px' }}><span className="dot" />{flag.label}</span>}
                          </div>
                          <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{u.role}</div>
                        </div>
                      </div>
                    </td>
                    {activeCats.map((c) => {
                      const n = counts[u.id]?.[c] || 0;
                      return (
                        <td key={c} className="num tabular" style={{
                          padding: '6px', borderBottom: '1px solid var(--border)',
                          color: n ? `var(--t-${toneOf(c)}-fg)` : 'var(--text-3)',
                          background: n ? `var(--t-${toneOf(c)}-bg)` : undefined,
                          fontWeight: n ? 700 : 400,
                          opacity: n ? 1 : 0.5,
                        }} title={`${u.name} · ${c}: ${n} task${n === 1 ? '' : 's'}`}>{n || '·'}</td>
                      );
                    })}
                    <td className="num tabular" style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', fontWeight: 700 }}>{userTotals[u.id]}</td>
                  </tr>
                );
              })}
              {/* Bottom total row (per category) */}
              <tr>
                <td style={{ padding: '8px 10px', borderTop: '2px solid var(--border)', position: 'sticky', left: 0, background: 'var(--surface-alt)', fontWeight: 700, zIndex: 1 }}>Total</td>
                {activeCats.map((c) => (
                  <td key={c} className="num tabular" style={{ padding: '8px 6px', borderTop: '2px solid var(--border)', background: 'var(--surface-alt)', fontWeight: 700, color: `var(--t-${toneOf(c)}-fg)` }}>{catTotals[c] || 0}</td>
                ))}
                <td className="num tabular" style={{ padding: '8px 10px', borderTop: '2px solid var(--border)', borderLeft: '1px solid var(--border)', background: 'var(--surface-alt)', fontWeight: 800 }}>{grandTotal}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* (B) Per-user TOTAL bar chart */}
      <div className="card">
        <div className="card-h"><h3>Total tasks per member</h3><div className="sub">Sorted heaviest first · width scaled to the busiest member</div></div>
        <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {barRows.map((u) => {
            const total = userTotals[u.id];
            const pct = (total / maxTotal) * 100;
            const flag = loadTone(total);
            const barColor = flag ? `var(--t-${flag.tone}-fg)` : 'var(--accent)';
            return (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 150, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 7, overflow: 'hidden' }}>
                  <Avatar user={u} size="sm" />
                  <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</span>
                </div>
                <div style={{ flex: 1, position: 'relative', height: 20, background: 'var(--surface-alt)', borderRadius: 4, overflow: 'hidden' }} title={`${u.name}: ${total} task${total === 1 ? '' : 's'}`}>
                  <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, minWidth: total ? 2 : 0, background: barColor, borderRadius: 4, opacity: 0.85, transition: 'width 0.2s' }} />
                </div>
                <div className="tabular" style={{ width: 56, flexShrink: 0, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}>
                  {flag && <span title={flag.label} style={{ width: 8, height: 8, borderRadius: '50%', background: `var(--t-${flag.tone}-fg)`, display: 'inline-block' }} />}
                  <span style={{ fontWeight: 700 }}>{total}</span>
                </div>
              </div>
            );
          })}
          {!team.length && <div style={{ color: 'var(--text-3)', fontSize: 12 }}>No team members.</div>}
        </div>
      </div>
    </div>
  );
}

function BurdenSummary({ cellMap, months, currentIdx }: any) {
  const D = useData();
  const nowIdx = Math.max(0, currentIdx ?? 0);
  const nowLabel = months[nowIdx] ?? 'this month';
  const totals = useMemo(() => {
    const monthTotals = months.map((_: string, i: number) => {
      let total = 0;
      D.team.forEach((u) => { total += ((cellMap[u.id] || [])[i] || []).length; });
      return total;
    });
    const overloadedThisMonth = D.team.filter((u) => ((cellMap[u.id] || [])[nowIdx] || []).length >= 5);
    const freeThisMonth = D.team.filter((u) => ((cellMap[u.id] || [])[nowIdx] || []).length === 0);
    const heaviest = D.team.map((u) => ({ u, total: (cellMap[u.id] || []).reduce((s: number, arr: any[]) => s + arr.length, 0) })).sort((a, b) => b.total - a.total)[0];
    return { monthTotals, overloadedThisMonth, freeThisMonth, heaviest };
  }, [cellMap, months, D.team, nowIdx]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 14 }}>
      <div className="stat">
        <div className="label">Active task-months</div>
        <div className="v tabular">{totals.monthTotals.reduce((a: number, b: number) => a + b, 0)}</div>
        <div className="d">across {D.team.length} team members, {months.length} months</div>
      </div>
      <div className="stat">
        <div className="label">Heaviest workload</div>
        <div className="v" style={{ fontSize: 16 }}>{totals.heaviest?.u.name}</div>
        <div className="d">{totals.heaviest?.total} task-months across the plan</div>
      </div>
      <div className="stat">
        <div className="label">Overloaded ({nowLabel})</div>
        <div className="v tabular">{totals.overloadedThisMonth.length}</div>
        <div className="d">team members with ≥ 5 tasks</div>
      </div>
      <div className="stat">
        <div className="label">Free in {nowLabel}</div>
        <div className="v tabular">{totals.freeThisMonth.length}</div>
        <div className="d">{totals.freeThisMonth.map((u) => u.initials).join(', ') || '—'} have capacity</div>
      </div>
    </div>
  );
}
